import {
  computed,
  IObservableArray,
  makeAutoObservable,
  observable,
} from "mobx";
import {
  EmptyLearningPathResponseImpl,
  LearningPathResponseApi,
  ObservableLearningPathPropImpl,
} from "../../DTO/ServerResponseType/LearningPath/LearningPath";
import {
  LearningPathModuleResponseApi,
  LearningPathModuleResponseImpl,
} from "../../DTO/ServerResponseType/LearningPath/LearningPathModule";
import LearningPathServerClient from "../../ServerClients/LearningPathServerClient";
import { CheckArgs } from "../../utils/checks/CheckArgsWithoutThrow";
import { ModuleResponseApi } from "../../DTO/ServerResponseType/Module/Module";
import getLearningPathNode from "../../ProfileWindow/AdminWindows/LearningPathListWindow/LearningPathListComponents/TreeComponents/LearningPathNode";
import { LearningPathRequestImpl } from "../../DTO/ClientRequest/LearningPath/LearningPathRequest";
import { LearningPathIdsApi } from "../../DTO/ServerResponseType/LearningPath/LearningPathIds";
import { ResourceIdAndNameResponseApi } from "../../DTO/ServerResponseType/Resource/Resource";
import { LearningPathResourceResponseApi } from "../../DTO/ServerResponseType/LearningPath/LearningPathResource";

export class LearningPathStore {
  learningPath: ObservableLearningPathPropImpl =
    new ObservableLearningPathPropImpl(new EmptyLearningPathResponseImpl());
  tempModules: IObservableArray<ModuleResponseApi> = observable.array([]);
  tempResources: IObservableArray<ResourceIdAndNameResponseApi> =
    observable.array([]);
  tempName: string = "";

  doSave: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  onChangeTempName = (event: React.FormEvent<HTMLInputElement>) => {
    this.tempName = event.currentTarget.value;
  };

  onChangeName = (name: string) => {
    this.learningPath.onChangeName(name);
  };

  onChangeDoSave = (doSave: boolean) => {
    this.doSave = doSave;
  };

  setLearningPath = (learningPath: LearningPathResponseApi) => {
    this.learningPath = new ObservableLearningPathPropImpl(learningPath);
    this.tempName = learningPath.learningPathName;
  };

  @computed get getLearningPathNode() {
    return getLearningPathNode(this);
  }

  onChangeNeighboringModuleOrder = (
    learningPathModule: LearningPathModuleResponseApi,
    newOrder: number
  ) => {
    if (!this.learningPath.learningPathModules.includes(learningPathModule)) {
      console.log(
        "Модуль пути обучения не найден: " +
          learningPathModule.module.moduleName
      );
      return;
    }

    let temp1 = new LearningPathModuleResponseImpl(
      this.learningPath.learningPathModules[
        learningPathModule.learningPathModuleOrder - 1
      ]
    );

    let temp2 = new LearningPathModuleResponseImpl(
      this.learningPath.learningPathModules[newOrder - 1]
    );

    // reordering

    temp1.learningPathModuleOrder = newOrder;
    temp2.learningPathModuleOrder = learningPathModule.learningPathModuleOrder;

    // swap
    this.learningPath.learningPathModules[
      learningPathModule.learningPathModuleOrder - 1
    ] = temp2;

    this.learningPath.learningPathModules[newOrder - 1] = temp1;
  };

  addTempModulesToLearningPathAsync = async () => {
    let modulesCount = this.learningPath.learningPathModules.length;
    this.tempModules.forEach((module, index) => {
      this.learningPath.pushModule(module, modulesCount + index + 1);
    });
  };

  addTempResourcesToLearningPathAsync = async () => {
    this.tempResources.forEach((resource) => {
      this.learningPath.pushResource(resource);
    });
  };

  setTempModules = (modules: ModuleResponseApi[]) => {
    this.tempModules.replace(modules);
  };

  setTempResources(resources: ResourceIdAndNameResponseApi[]) {
    this.tempResources.replace(resources);
  }

  setLearningPathModuleIds = (ids: string[]) => {
    this.learningPath.learningPathModules.forEach(
      (learningPathModule: LearningPathModuleResponseApi, index) => {
        learningPathModule.learningPathModuleId = ids[index];
      }
    );
  };

  setLearningPathResourceIds(ids: string[]) {
    this.learningPath.learningPathResources.forEach(
      (learningPathResource, index) => {
        learningPathResource.learningPathResourceId = ids[index];
      }
    );
  }

  saveLearningPathAsync = async () => {
    let req = new LearningPathRequestImpl(this.learningPath);
    let savePromise;
    if (
      CheckArgs.CheckNullOrUndefinedOrEmptyString(
        this.learningPath.learningPathId
      )
    ) {
      savePromise = LearningPathServerClient.sendToCreateLearningPathAsync(
        req
      ).then((learningPathId: string) => this.setId(learningPathId));
    } else {
      savePromise = LearningPathServerClient.sendToUpdateLearningPathAsync(
        this.learningPath.learningPathId,
        req
      ).then((learningPathIds: LearningPathIdsApi) => {
        this.setLearningPathModuleIds(learningPathIds.learningPathModuleIds);
        this.setLearningPathResourceIds(
          learningPathIds.learningPathResourceIds
        );
      });
    }

    return savePromise;
  };

  deleteLearningPathModuleAsync = async (moduleId: string) => {
    let learningPathModule: LearningPathModuleResponseApi | undefined =
      this.learningPath.learningPathModules.find(
        (learningPathModule) => learningPathModule.module.moduleId === moduleId
      );

    if (learningPathModule === undefined) {
      alert("Указанного модуля нет в указанном пути обучения");
      return;
    }

    let learningPathOrder = learningPathModule.learningPathModuleOrder;

    return this.learningPath.onChangeModules(
      this.learningPath.learningPathModules
        .filter(
          (learningPathModule: LearningPathModuleResponseApi) =>
            learningPathModule.module.moduleId !== moduleId
        )
        .map((maplearningPathModule) => {
          if (maplearningPathModule.learningPathModuleOrder > learningPathOrder)
            maplearningPathModule.learningPathModuleOrder--;

          return maplearningPathModule;
        })
    );
  };

  async deleteLearningPathResourceAsync(resourceId: string) {
    let learningPathResource: number =
      this.learningPath.learningPathResources.findIndex(
        (learningPathResource) =>
          learningPathResource.resource.resourceId === resourceId
      );

    if (learningPathResource === -1) {
      alert("Указанного ресурса нет в указанном пути обучения");
      return;
    }

    this.learningPath.learningPathResources.splice(learningPathResource, 1);

    return this.learningPath.onChangeResources(
      this.learningPath.learningPathResources
    );
  }

  setId = (learningPathId: string) => {
    this.learningPath.onChangeId(learningPathId);
  };
}
